import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Paper,
  IconButton,
  Button
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { ContentSettings } from './SiteCustomizationManager';

interface ContentEditorProps {
  content: ContentSettings;
  onChange: (content: ContentSettings) => void;
}

export const ContentEditor: React.FC<ContentEditorProps> = ({ content, onChange }) => {
  const handleWelcomeChange = (field: keyof ContentSettings['welcome'], value: string) => {
    onChange({
      ...content,
      welcome: {
        ...content.welcome,
        [field]: value
      }
    });
  };

  const handleFooterChange = (field: keyof Omit<ContentSettings['footer'], 'socialLinks'>, value: string) => {
    onChange({
      ...content,
      footer: {
        ...content.footer,
        [field]: value
      }
    });
  };

  const handleSocialLinkChange = (platform: string, value: string) => {
    onChange({
      ...content,
      footer: {
        ...content.footer,
        socialLinks: {
          ...content.footer.socialLinks,
          [platform]: value
        }
      }
    });
  };

  const handleAddCustomLink = () => {
    const customLinks = content.footer.socialLinks.custom || [];
    onChange({
      ...content,
      footer: {
        ...content.footer,
        socialLinks: {
          ...content.footer.socialLinks,
          custom: [
            ...customLinks,
            { name: '', url: '', icon: '' }
          ]
        }
      }
    });
  };

  const handleCustomLinkChange = (index: number, field: 'name' | 'url' | 'icon', value: string) => {
    const customLinks = [...(content.footer.socialLinks.custom || [])];
    customLinks[index] = {
      ...customLinks[index],
      [field]: value
    };
    onChange({
      ...content,
      footer: {
        ...content.footer,
        socialLinks: {
          ...content.footer.socialLinks,
          custom: customLinks
        }
      }
    });
  };

  const handleRemoveCustomLink = (index: number) => {
    const customLinks = [...(content.footer.socialLinks.custom || [])];
    customLinks.splice(index, 1);
    onChange({
      ...content,
      footer: {
        ...content.footer,
        socialLinks: {
          ...content.footer.socialLinks,
          custom: customLinks
        }
      }
    });
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Content Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Customize the text content displayed to guests throughout the site.
      </Typography>

      <Grid container spacing={3}>
        {/* Welcome Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Welcome Section
            </Typography>
            
            <TextField
              fullWidth
              label="Welcome Title"
              value={content.welcome.title}
              onChange={(e) => handleWelcomeChange('title', e.target.value)}
              helperText={`${content.welcome.title.length}/100 characters`}
              inputProps={{ maxLength: 100 }}
              sx={{ mb: 3 }}
            />

            <TextField
              fullWidth
              label="Tagline"
              value={content.welcome.tagline}
              onChange={(e) => handleWelcomeChange('tagline', e.target.value)}
              helperText={`${content.welcome.tagline.length}/50 characters`}
              inputProps={{ maxLength: 50 }}
              sx={{ mb: 3 }}
            />

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Welcome Message"
              value={content.welcome.message}
              onChange={(e) => handleWelcomeChange('message', e.target.value)}
              helperText={`${content.welcome.message.length}/500 characters. Basic HTML tags supported.`}
              inputProps={{ maxLength: 500 }}
            />
          </Paper>
        </Grid>

        {/* Footer Contact Information */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Contact Information
            </Typography>
            
            <TextField
              fullWidth
              label="Contact Email"
              type="email"
              value={content.footer.contactEmail || ''}
              onChange={(e) => handleFooterChange('contactEmail', e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Contact Phone"
              type="tel"
              value={content.footer.contactPhone || ''}
              onChange={(e) => handleFooterChange('contactPhone', e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              multiline
              rows={2}
              label="Address"
              value={content.footer.address || ''}
              onChange={(e) => handleFooterChange('address', e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Copyright Text"
              value={content.footer.copyrightText || ''}
              onChange={(e) => handleFooterChange('copyrightText', e.target.value)}
              placeholder={`© ${new Date().getFullYear()} All Rights Reserved`}
              helperText="Customize the copyright text shown in the footer"
            />
          </Paper>
        </Grid>

        {/* Social Media Links */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Social Media Links
            </Typography>
            
            <TextField
              fullWidth
              label="Facebook URL"
              value={content.footer.socialLinks.facebook || ''}
              onChange={(e) => handleSocialLinkChange('facebook', e.target.value)}
              placeholder="https://facebook.com/yourpage"
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Instagram URL"
              value={content.footer.socialLinks.instagram || ''}
              onChange={(e) => handleSocialLinkChange('instagram', e.target.value)}
              placeholder="https://instagram.com/yourprofile"
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Twitter URL"
              value={content.footer.socialLinks.twitter || ''}
              onChange={(e) => handleSocialLinkChange('twitter', e.target.value)}
              placeholder="https://twitter.com/yourhandle"
            />
          </Paper>
        </Grid>

        {/* Custom Social Links */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Custom Links
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddCustomLink}
                variant="outlined"
                size="small"
              >
                Add Link
              </Button>
            </Box>

            {content.footer.socialLinks.custom && content.footer.socialLinks.custom.length > 0 ? (
              content.footer.socialLinks.custom.map((link, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
                  <TextField
                    label="Link Name"
                    value={link.name}
                    onChange={(e) => handleCustomLinkChange(index, 'name', e.target.value)}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="URL"
                    value={link.url}
                    onChange={(e) => handleCustomLinkChange(index, 'url', e.target.value)}
                    size="small"
                    sx={{ flex: 2 }}
                  />
                  <TextField
                    label="Icon (optional)"
                    value={link.icon || ''}
                    onChange={(e) => handleCustomLinkChange(index, 'icon', e.target.value)}
                    size="small"
                    sx={{ flex: 1 }}
                    placeholder="emoji or icon name"
                  />
                  <IconButton
                    onClick={() => handleRemoveCustomLink(index)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No custom links added yet. Click "Add Link" to create one.
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Preview */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Content Preview
            </Typography>
            <Box sx={{ p: 3, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="h4" gutterBottom>
                {content.welcome.title}
              </Typography>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {content.welcome.tagline}
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {content.welcome.message}
              </Typography>
              
              <Box sx={{ borderTop: '1px solid #ddd', pt: 2, mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Footer Preview:
                </Typography>
                {content.footer.contactEmail && (
                  <Typography variant="body2">Email: {content.footer.contactEmail}</Typography>
                )}
                {content.footer.contactPhone && (
                  <Typography variant="body2">Phone: {content.footer.contactPhone}</Typography>
                )}
                {content.footer.address && (
                  <Typography variant="body2">Address: {content.footer.address}</Typography>
                )}
                {content.footer.copyrightText && (
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                    {content.footer.copyrightText}
                  </Typography>
                )}
                {(content.footer.socialLinks.facebook || 
                  content.footer.socialLinks.instagram || 
                  content.footer.socialLinks.twitter ||
                  (content.footer.socialLinks.custom && content.footer.socialLinks.custom.length > 0)) && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" fontWeight="bold">Social Links:</Typography>
                    {content.footer.socialLinks.facebook && (
                      <Typography variant="body2">• Facebook</Typography>
                    )}
                    {content.footer.socialLinks.instagram && (
                      <Typography variant="body2">• Instagram</Typography>
                    )}
                    {content.footer.socialLinks.twitter && (
                      <Typography variant="body2">• Twitter</Typography>
                    )}
                    {content.footer.socialLinks.custom?.map((link, idx) => (
                      link.name && <Typography key={idx} variant="body2">• {link.name}</Typography>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
